import ThemeConstants from "../../../../config/theme/theme_constants";
import { SyncLoader } from "react-spinners";
import NewsElement from "../../../../core/components/news/presentation/news_element";
import { useGetNews } from "../../../../core/components/news/domain/hooks/use_get_news";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "../../../../i18n/useTranslation";

const NewsGrid: React.FC = () => {
    const { t } = useTranslation();
    const theme = ThemeConstants();
    const loadMoreRef = useRef<HTMLDivElement>(null);

    const { 
        allNews, 
        isLoading, 
        error, 
        refetch, 
        fetchNextPage, 
        hasNextPage, 
        isFetchingNextPage 
    } = useGetNews();

    const handleLoadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    handleLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        const loadMoreElement = loadMoreRef.current;
        if (loadMoreElement) {
            observer.observe(loadMoreElement);
        }

        return () => {
            if (loadMoreElement) {
                observer.unobserve(loadMoreElement);
            }
        };
    }, [handleLoadMore]);

    return (
        <div className="bg-white flex-1 rounded-t-[30px] px-3 py-7 flex flex-col gap-8 overflow-x-clip">
            {isLoading
                ? <SyncLoader color={theme.UvibesPink} size={24} speedMultiplier={0.5}/>
                : error !== null
                    ? <div className="flex flex-col items-center gap-5">
                        <p className="font-semibold text-center text-sm">{t('common.error.fetch_news')}</p>
                        <button
                            className="rounded-full text-white font-bold px-8 py-2.5 min-h-10.5 min-w-51 cursor-pointer"
                            style={{ backgroundColor: theme.UvibesPink }}
                            onClick={() => {refetch();}}
                            >
                            {t('common.button.retry')}
                        </button>
                    </div>
                    : allNews.length === 0
                        ? <p className="font-semibold text-center text-sm">{t('news.empty')}</p>
                        : <>
                            <div className="grid grid-cols-[repeat(auto-fit,_minmax(min(378px,_100%),_378px))] gap-x-4 gap-y-3 justify-center">
                              {allNews.map((item) => (
                                <NewsElement key={item.id} news={item}/>
                            ))}
                            </div>
                            
                            {hasNextPage && (
                                <div ref={loadMoreRef} className="flex justify-center py-4">
                                    {isFetchingNextPage ? (
                                        <SyncLoader color={theme.UvibesPink} size={16} speedMultiplier={0.5}/>
                                    ) : (
                                        <button 
                                            onClick={handleLoadMore}
                                            className="text-sm font-medium px-6 py-2 rounded-full border border-gray-300 hover:bg-gray-50"
                                        >
                                            {t('news.load_more')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
            }
        </div>
    );
}

export default NewsGrid;